import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { KozosCegService } from '../kozos-ceg.service';

/*  INVENTARIO – az aktív cég (könyvelési ügyfél), a szerep rajta és az év (a Számla és Kassza párja).
 *
 *  SZEREP: 'ugyfel' – a felhasználó a cég nevében dolgozik (ír); 'konyvelo' – a könyvelőiroda
 *  szemével látja (csak olvas). A kiszolgáló dönti el (ugyfel-hozzaferes.js), itt csak megjelenítjük.
 *  A cég a többi modullal közös (KozosCegService): a Könyvelésből átlépve ugyanaz a cég nyílik. */
export interface InventarioAktiv {
  intezmeny: string;
  ugyfelId: string | null;
  ugyfelNev: string | null;
  szerep: 'ugyfel' | 'konyvelo' | null;
  ev: number;
}

@Injectable({ providedIn: 'root' })
export class InventarioService {
  aktiv$ = new BehaviorSubject<InventarioAktiv>({ intezmeny: '', ugyfelId: null, ugyfelNev: null, szerep: null, ev: new Date().getFullYear() });
  get aktiv(): InventarioAktiv { return this.aktiv$.value; }
  get ir(): boolean { return this.aktiv.szerep === 'ugyfel'; }
  private kesz = false;
  readonly afaKulcsok = [{ kod: '27', nev: '27%' }, { kod: '18', nev: '18%' }, { kod: '5', nev: '5%' }, { kod: '0', nev: '0%' }, { kod: 'AAM', nev: 'AAM' }, { kod: 'TAM', nev: 'TAM' }];

  constructor(private http: HttpClient, private data: DataService, private kozos: KozosCegService) {}

  async init(): Promise<InventarioAktiv> {
    if (this.kesz) return this.aktiv;
    const si = await this.data.getFromStorage('sajatIntezmeny');
    const m = (await this.data.getFromStorage('inventarioAktiv')) || {};
    this.aktiv$.next({ intezmeny: si?.intezmenyId || '', ugyfelId: m.ugyfelId || null, ugyfelNev: m.ugyfelNev || null,
      szerep: m.szerep || null, ev: m.ev || new Date().getFullYear() });
    this.kesz = true;
    return this.aktiv;
  }
  async setAktiv(patch: Partial<InventarioAktiv>) {
    const a = { ...this.aktiv, ...patch };
    this.aktiv$.next(a);
    await this.data.setStorage('inventarioAktiv', { ugyfelId: a.ugyfelId, ugyfelNev: a.ugyfelNev, szerep: a.szerep, ev: a.ev });
    if (a.ugyfelId) await this.kozos.jelol('INVENTARIO', a.ugyfelId);
  }
  /** A látható cégek (szereppel) – az ügyfél csak a sajátjait, a könyvelő mindet. */
  ugyfelek(): Promise<any[]> { return this.get(`/ugyfelek/${this.aktiv.intezmeny}`); }
  /*  Modulba lépéskor: a másik modulban épp aktív cég (ha itt is látható), különben a legutóbbi;
   *  ha az sem látható már (pl. elvett kötés), az első látható cég. A szerep mindig frissül. */
  async kozosAtvesz(): Promise<void> {
    let lista: any[] = [];
    try { lista = await this.ugyfelek(); } catch { return; }
    const atv = await this.kozos.atveendo('INVENTARIO');
    const u = (atv && lista.find((x) => x.id === atv)) || lista.find((x) => x.id === this.aktiv.ugyfelId)
      || lista.find((x) => x.szerep === 'ugyfel' && !x.inaktiv) || (lista.length === 1 ? lista[0] : null);
    if (u) await this.setAktiv({ ugyfelId: u.id, ugyfelNev: u.nev, szerep: u.szerep });
    else if (this.aktiv.ugyfelId) await this.setAktiv({ ugyfelId: null, ugyfelNev: null, szerep: null });
  }

  private url(p: string) { return `/api/inventario${p}`; }
  get<T = any>(p: string): Promise<T> { return firstValueFrom(this.http.get<T>(this.url(p))); }
  post<T = any>(p: string, body: any = {}): Promise<T> { return firstValueFrom(this.http.post<T>(this.url(p), body)); }
  put<T = any>(p: string, body: any = {}): Promise<T> { return firstValueFrom(this.http.put<T>(this.url(p), body)); }
  del<T = any>(p: string): Promise<T> { return firstValueFrom(this.http.delete<T>(this.url(p))); }
  megnyit(p: string, cim = 'Dokumentum') { return this.data.megnyitFajl(this.url(p), cim); }
  /** POST-tal kért fájl (címke-PDF, export) – a tokennel töltjük le, és új lapon nyitjuk / mentjük. */
  async postFajl(p: string, body: any, fajlnev: string, letolt = false) {
    const lap = letolt ? null : window.open('', '_blank');
    const blob = await firstValueFrom(this.http.post(this.url(p), body, { responseType: 'blob' }));
    const url = URL.createObjectURL(blob);
    if (lap) lap.location.href = url;
    else { const a = document.createElement('a'); a.href = url; a.download = fajlnev; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
  /** A címkék nyomtatható HTML-je (a közvetlen nyomtatáshoz – ugyanaz, amiből a PDF készül). */
  cimkeHtml(body: any): Promise<string> { return firstValueFrom(this.http.post(this.url(`/cimkek/${this.aktiv.ugyfelId}?html=1`), body, { responseType: 'text' })); }
  /** Az asztali program nyomtató-hídja (fo-preload.js) – weben nincs. */
  get nyomtatoHid(): any { return (window as any).cronomitNyomtato || null; }
  async getFajl(p: string, fajlnev: string) {
    const blob = await firstValueFrom(this.http.get(this.url(p), { responseType: 'blob' }));
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = fajlnev; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}
